import { Alert, Badge, Button, ErrorState, KeyRoundIcon, MailIcon, Skeleton } from "@strust/ui";
import { css } from "@strust/ui/css";
import { link } from "@strust/ui/recipes";
import { useIntl } from "react-intl";
import type { InvitationViewV1 } from "@strust/contracts";
import {
  invitationAccountMessage,
  invitationExpiresMessage,
  invitationMessages,
  invitationValidMessage,
} from "../messages.ts";
import { AuthHeading, AuthLayout, AuthNote } from "./auth-layout.tsx";
import { InvitationForm } from "./invitation-form.tsx";

const KIND_MESSAGES = {
  account: invitationMessages.kindAccount,
  password_setup: invitationMessages.kindPasswordSetup,
  platform_administrator: invitationMessages.kindPlatformAdministrator,
  two_factor_recovery: invitationMessages.kindTwoFactorRecovery,
} as const;

const DETAILS = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.5",
  m: "0",
  textStyle: "caption",
  color: "text.2",
});
const MONO = css({ fontFamily: "mono", color: "text" });
const FULL = css({ width: "full" });
const BADGE_SLOT = css({ display: "flex" });

export type InvitationScreenProps =
  | { readonly state: "loading" }
  | {
      /** Expired, revoked, already used or unknown: a new invitation is needed. */
      readonly state: "unavailable";
      readonly message: string;
      /** Link to the sign-in screen (router link rendered by the container). */
      readonly signInLink: React.ReactNode;
    }
  | {
      readonly state: "failed";
      readonly message: string;
      readonly onRetry: () => void;
    }
  | {
      readonly state: "ready";
      readonly invitation: Pick<
        InvitationViewV1,
        "kind" | "email" | "expires_at" | "sign_in_method"
      >;
      /** Hours left when the page opened. */
      readonly hoursLeft: number;
      readonly pending: boolean;
      readonly error?: string | undefined;
      readonly commonRefused?: boolean;
      readonly onSubmit: (password: string) => void;
      readonly onSso: () => void;
    };

/** Style of the link a container renders in the `unavailable` state. */
export const invitationLinkClass = link();

/** The invitation screen in each of its states; presentational (the container fetches). */
export function InvitationScreen(props: InvitationScreenProps) {
  const intl = useIntl();
  const title = intl.formatMessage(invitationMessages.title);

  if (props.state === "loading") {
    return (
      <AuthLayout busy>
        <AuthHeading title={title} />
        <p role="status" className={DETAILS}>
          {intl.formatMessage(invitationMessages.loading)}
        </p>
        <Skeleton height="2rem" />
      </AuthLayout>
    );
  }

  if (props.state === "unavailable") {
    return (
      <AuthLayout>
        <AuthHeading title={title} />
        <Alert tone="danger">{props.message}</Alert>
        <p>{props.signInLink}</p>
      </AuthLayout>
    );
  }

  if (props.state === "failed") {
    return (
      <AuthLayout>
        <AuthHeading title={title} />
        <ErrorState
          title={props.message}
          retryLabel={intl.formatMessage(invitationMessages.retry)}
          onRetry={props.onRetry}
        />
      </AuthLayout>
    );
  }

  const { invitation } = props;
  return (
    <AuthLayout>
      <AuthHeading
        title={title}
        subtitle={intl.formatMessage(KIND_MESSAGES[invitation.kind])}
        badge={
          <span className={BADGE_SLOT}>
            <Badge tone="info" icon={<MailIcon />}>
              {intl.formatMessage(invitationValidMessage, { hours: props.hoursLeft })}
            </Badge>
          </span>
        }
      />
      <p className={DETAILS}>
        <span className={MONO}>
          {intl.formatMessage(invitationAccountMessage, { email: invitation.email })}
        </span>
        <span>
          {intl.formatMessage(invitationExpiresMessage, {
            expiresAt: intl.formatDate(invitation.expires_at, {
              dateStyle: "medium",
              timeStyle: "short",
            }),
          })}
        </span>
      </p>
      {invitation.sign_in_method === "sso" ? (
        <>
          <Alert>{intl.formatMessage(invitationMessages.ssoHint)}</Alert>
          <Button className={FULL} visual="primary" icon={<KeyRoundIcon />} onClick={props.onSso}>
            {intl.formatMessage(invitationMessages.ssoSubmit)}
          </Button>
        </>
      ) : (
        <InvitationForm
          pending={props.pending}
          error={props.error}
          {...(props.commonRefused === undefined ? {} : { commonRefused: props.commonRefused })}
          onSubmit={props.onSubmit}
        />
      )}
      <AuthNote>{intl.formatMessage(invitationMessages.singleUse)}</AuthNote>
    </AuthLayout>
  );
}
